import {Form, Formik, FormikProps} from 'formik';
import {forEach, isEqual, map} from 'lodash';
import {useCallback, useEffect, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {userSettingsApi} from 'api';
import {AssignmentSelectorsFormik, Button, InformationModal, InputFormik, MultiInputFormik, PredefinedNotifierType} from 'components';
import {useApiData} from 'hooks';
import {Group} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';
import {ApiError} from 'types';
import {apiUtils, isEmailValid} from 'utils';

const T_KEYS = TRANSLATION_KEYS.USER.ACCOUNT.SESSION.REQUEST_APPOINTMENT_SECTION.RECIPIENT_EMAILS_MODAL;

type FormFields = {
    recipients: string[];
    group: {
        id: string;
    }
};

type FormTemplateProps = {
    groups: Group[];
    loading: boolean;
    isEditing: boolean;
    showAllGroupsOption: boolean;
    error: ApiError;
    handleClose: () => void;
} & FormikProps<FormFields>;

const FormTemplate: React.FC<FormTemplateProps> = ({
    values, initialValues, groups, loading, error,
    isEditing, showAllGroupsOption, handleClose, setFieldError
}: FormTemplateProps) => {
    const {t} = useTranslation();

    useEffect(() => {
        const err = apiUtils.getFieldsValidationErrorMessages(error, t,
            map(values.recipients, (v, index) => ({field: `recipients[${index}]`})));
        forEach(err, (value, key) => value && setFieldError(key, value as string));
    }, [t, setFieldError, error, values]);

    return (
        <Form>
            <div className='tw-mt-10'>
                {!isEditing &&
                    <AssignmentSelectorsFormik
                        name='group'
                        groupIdProp='id'
                        groups={groups}
                        collapseEmptyOption={false}
                        emptyGroupOption={showAllGroupsOption ? 'all' : null}
                    >
                        {({renderGroup}) => (
                            <div className='tw-mr-10'>
                                {renderGroup()}
                            </div>
                        )}
                    </AssignmentSelectorsFormik>}

                <MultiInputFormik
                    name='recipients'
                    itemsPath='recipients'
                    defaultValue=''
                    values={values}
                    isValid={(item) => isEmailValid(item) && !values.recipients.includes(item)}
                >
                    {({index, item, path, renderActions}) => (
                        <div key={index} className='tw-flex tw-items-baseline tw-mb-4'>
                            <InputFormik className='tw-flex-grow' name={path} label={t(T_KEYS.FORM_CONTROLS.EMAIL)} />
                            <div className='tw-w-10 -lg:tw-mt-6'>{renderActions({index, item})}</div>
                        </div>
                    )}
                </MultiInputFormik>
            </div>
            <div className='tw-text-right tw-mt-8 tw-mb-4 lg:tw-mr-10'>
                <Button
                    type='button'
                    className='tw-ml-3'
                    width='small'
                    disabled={loading}
                    onClick={handleClose}
                >
                    {t(T_KEYS.BUTTONS.CANCEL)}
                </Button>
                <Button
                    type='submit'
                    color='primary'
                    className='tw-ml-3'
                    width='small'
                    disabled={loading || isEqual(values.recipients, initialValues.recipients)}
                >
                    {t(T_KEYS.BUTTONS.SAVE)}
                </Button>
            </div>
        </Form>
    );
};

type Props = {
    group?: Group,
    isEditing: boolean;
    showAllGroupsOption: boolean;
    open: boolean;
    groups: Group[];
    recipients: string[];
    onClose: (recipientsUpdated: boolean) => void;
}

export const RecipientModal: React.FC<Props> = ({
    open, isEditing, showAllGroupsOption,
    group, groups, recipients, onClose
}: Props) => {
    const {t} = useTranslation();
    const {send: saveRecipients, loading, error, reset} = useApiData(userSettingsApi.updateRequestAppointmentRecipients, {
        notificationType: PredefinedNotifierType.Save,
        onSuccess: () => {
            onClose(true);
        }
    });

    const title = useMemo(() => {
        const titleEdit = group?.name ?? t(T_KEYS.APPLY_ALL_GROUPS);
        return isEditing ? `${t(T_KEYS.TITLE_EDIT)} ${titleEdit}` : t(T_KEYS.TITLE_ADD);
    }, [isEditing, group, t]);

    const handleSubmit = useCallback((values: {recipients: string[], group: {id: string}}) => {
        const groupId = values.group.id || group?.id;
        saveRecipients(values.recipients, groupId);
    }, [group, saveRecipients]);

    const handleClose = useCallback(() => {
        onClose(false);
    }, [onClose]);

    const initialValues = useMemo<FormFields>(() => ({
        recipients,
        group: {
            id: isEditing ? group?.id ?? '' : groups[0]?.id ?? ''
        }
    }), [recipients, group, groups, isEditing]);

    useEffect(() => {
        if (!open) {
            reset();
        }
    }, [open, reset]);

    return (
        <InformationModal
            open={open}
            title={title}
            fullWidth
            maxWidth='sm'
            onClose={handleClose}
        >
            <Formik
                initialValues={initialValues}
                validateOnBlur={false}
                validateOnChange={false}
                onSubmit={handleSubmit}
            >
                {(props) => (
                    <FormTemplate
                        {...props}
                        error={error}
                        groups={groups}
                        loading={loading}
                        isEditing={isEditing}
                        showAllGroupsOption={showAllGroupsOption}
                        handleClose={handleClose}
                    />
                )}
            </Formik>
        </InformationModal>
    );
};
